import {
  Card,
  HStack,
  Heading,
  Icon,
  Spinner,
  Stack,
  Tag,
  Text,
  useDisclosure,
  Menu,
  Portal,
  Dialog,
  Tabs,
  SkeletonText,
  Code,
} from '@chakra-ui/react';
import { Button } from './ui/button';
import { toaster } from '../components/ui/toaster';
import { useGetNodeQuery, useGetSummaryQuery } from '../lib/api/GraphAPI';
import { useAppDispatch, useAppSelector } from '../lib/hooks';
import { motion } from 'framer-motion';
import { SummaryType } from '../lib/api/APITypes';
import { getDefaultGraphView } from '../lib/GraphFactory';
import { addNewGraphView, setDocumentStructure } from '../lib/state/slices/ProjectStateSlice';
import { CSSProperties, useEffect, useState } from 'react';
import {
  ChevronRight,
  ExternalLink,
  Folder as FolderIcon,
  MoreVertical,
  PenTool,
  X,
} from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { AISparcles } from './Icons';
import { Tooltip } from './ui/tooltip';
import StyledButton from './ui/styledButton';
import { Article, Importance } from '@/lib/state/ViewTypes';

export default function DocumentCard(props: {
  id: string;
  nodeId?: string;
  collapsedState?: 'open' | 'collapsed';
  onClose?: () => void;
  style?: CSSProperties;
}) {
  const [cardState, setCardState] = useState<'open' | 'collapsed'>('collapsed');

  const contextMenuDisclosure = useDisclosure();
  const zoteroModalDisclosure = useDisclosure();
  const article6ModalDisclosure = useDisclosure();

  const dispatch = useAppDispatch();

  const {
    data: nodeData,
    error: nodeError,
    isLoading: nodeIsLoading,
    isError: nodeIsError,
  } = useGetNodeQuery(props.id);

  const handleSave = (event: React.MouseEvent, folderId: string, folderName: string) => {
    let folder = folders.find((folder) => folder.id === folderId);
    if (!folder) return;

    // Check if Document is already in the folder
    for (const docid of folder.documents) {
      if (docid === props.id) {
        return;
      }
    }

    dispatch(
      setDocumentStructure({
        type: 'FOLDER',
        key: folderId,
        value: { id: folderId, name: folderName, documents: [...folder.documents, props.id] },
      }),
    );

    event?.stopPropagation();

    toaster.create({
      title: 'Document saved',
      description: `The document has been saved to ${folderName}`,
      type: 'info',
      duration: 3000,
      closable: true,
    });
  };

  const folders = Object.values(
    useAppSelector((state) => state.viewReducer.documents.structure.FOLDER),
  );

  useEffect(() => {
    if (zoteroModalDisclosure.open && nodeData) {
      console.log('Adding citation metadata');
      const head = document.head;

      head.insertAdjacentHTML(
        'beforeend',
        `<meta name="citation_title" content="${nodeData.label}"/>` +
        `<meta name="DC.type" content="case" />` +
        `<meta name="citation_date" content="${new Date(nodeData.date).toLocaleDateString()}"/>` +
        `<meta name="citation_language" content="en"/>` +
        `<meta name="citation_public_url" content="${nodeData.url || ''}"/>`,
      );
      document.dispatchEvent(
        new CustomEvent('ZoteroItemUpdated', { bubbles: true, cancelable: true }),
      );
    }

    if (!zoteroModalDisclosure.open) {
      const head = document.head;
      const metaTags = head.querySelectorAll('meta[name^="citation"], meta[name^="DC"]');
      metaTags.forEach((meta) => head.removeChild(meta));
      document.dispatchEvent(
        new CustomEvent('ZoteroItemUpdated', { bubbles: true, cancelable: true }),
      );
    }
  }, [zoteroModalDisclosure.open, nodeData]);

  useEffect(() => {
    if (nodeIsError) {
      toaster.create({
        title: 'Error fetching document',
        description: `Something went wrong while fetching this document: ${nodeError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [nodeIsError, nodeError]);

  function openInGraph() {
    let newGraphView = getDefaultGraphView(props.id);
    newGraphView.name = nodeData ? nodeData.label.replace('CASE OF', '') : 'Graph ' + props.id;
    dispatch(
      setDocumentStructure({
        type: 'GRAPH',
        key: newGraphView.id,
        value: newGraphView,
      }),
    );
    dispatch(addNewGraphView(newGraphView));
    navigate('/graphs/' + newGraphView.id);
  }
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, height: '0', x: -100 }}
      animate={{ opacity: 1, height: 'auto', x: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      style={{ ...props.style, zIndex: 100 }}
    >
      <Card.Root
        backgroundColor="cardDefault"
        _hover={{ bg: 'cardHover', transition: 'background-color 0.2s', cursor: 'pointer' }}
        border="none"
        width={'390px'}
        style={{ boxShadow: '0px 4px 10px -4px #00000080' }}
        position={'relative'}
      >
        <Card.Body
          id={`card-${props.id}`}
          onClick={() => {
            if (cardState === 'open') setCardState('collapsed');
            else if (cardState === 'collapsed') setCardState('open');
          }}
        >
          <Stack>
            <Stack direction={'row'} justifyContent={'space-between'}>
              {nodeIsLoading ? (
                <SkeletonText />
              ) : (
                <Heading color="primaryText" size="md" whiteSpace={'normal'}>
                  {nodeData ? nodeData.label : 'Something went wrong'}
                </Heading>
              )}
              <Stack direction={'row'}>
                <Tag.Root height={'fit-content'} backgroundColor="accentBlue" color={'white'}>
                  {nodeData?.id.split(':')[0].toUpperCase()}
                </Tag.Root>
                <CardMenu />
              </Stack>
            </Stack>
            <CardTags />
            {nodeData ? (
              <HStack>
                <Text fontSize="14px" fontWeight={600} color="secondaryText">
                  Date:{' '}
                  {new Date(nodeData.date!).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    year: 'numeric',
                    month: '2-digit',
                  })}{' '}
                </Text>
                <Text fontSize="14px" fontWeight={600} color="secondaryText">
                  |
                </Text>
                {nodeData.specific_properties.appno && (
                  <Text fontSize="14px" fontWeight={600} color="secondaryText">
                    Appno:{' '}
                    {
                      <Tooltip content={nodeData.specific_properties.appno?.replaceAll(';', '  ')}>
                        <>
                          {' '}
                          {nodeData.specific_properties.appno!.length > 10
                            ? nodeData.specific_properties.appno?.substring(0, 10) + '...'
                            : nodeData.specific_properties.appno}
                        </>
                      </Tooltip>
                    }
                  </Text>
                )}
              </HStack>
            ) : (
              <Spinner />
            )}
            {nodeIsLoading ? (
              <SkeletonText />
            ) : cardState === 'open' ? (
              <Tabs.Root defaultValue={SummaryType.GENERAL} lazyMount>
                <Tabs.List>
                  <Tabs.Trigger
                    color="secondaryText"
                    border="red"
                    css={{
                      '&before': {
                        borderColor: 'red',
                      },
                    }}
                    value={SummaryType.GENERAL}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  >
                    General
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    color="secondaryText"
                    value={SummaryType.FACTS}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  >
                    Facts
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    color="secondaryText"
                    value={SummaryType.LAW}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  >
                    Law
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    color="secondaryText"
                    value={SummaryType.CONCLUSION}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  >
                    Conclusion
                  </Tabs.Trigger>
                </Tabs.List>
                {Object.values(SummaryType).map((type) => (
                  <Tabs.Content key={type} value={type} color="secondaryText">
                    <SummaryText id={props.id} type={type} />
                  </Tabs.Content>
                ))}
              </Tabs.Root>
            ) : null}
          </Stack>
        </Card.Body>
        {cardState === 'open' ? (
          <Card.Footer>
            <HStack width="100%" justifyContent={'flex-end'}>
              <StyledButton
                id={`open-graph-${props.id}`}
                variant="solid"
                text="Open Graph"
                onClick={() => {
                  openInGraph();
                  toaster.create({
                    title: 'Graph opened',
                    description: 'Your Graph has been opened in the sidebar',
                    type: 'info',
                    duration: 3000,
                    closable: true,
                  });
                }}
              />
            </HStack>
          </Card.Footer>
        ) : null}
      </Card.Root>

      <Dialog.Root
        open={zoteroModalDisclosure.open}
        onOpenChange={() => zoteroModalDisclosure.setOpen(!zoteroModalDisclosure.open)}
      >
        <Portal>
          <Dialog.Positioner>
            <Dialog.Backdrop />
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Zotero Connector</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  You can now use the Zotero connector to cite <br /> {nodeData?.label}.
                  Alternatively, use the following Bibtex code.
                </Text>
                <Code
                  flexDirection={'column'}
                  justifyContent={'flex-start'}
                  alignItems={'start'}
                  padding={2}
                  marginTop={2}
                >
                  {` @misc\\{${nodeData?.label
                    .replace('CASE OF ', '')
                    .toLowerCase()
                    .replaceAll(' ', '_')
                    .replaceAll('.', '')}, \n`}
                  <div style={{ marginLeft: 20 }}>
                    {`title = {${nodeData?.name}}, `}
                    <br />
                    {`url = {${nodeData?.url}}, `}
                    <br />
                    {'language = {en}, '}
                    <br />
                    {`urldate = {${new Date().toISOString()}}, `}
                    <br />
                    {`month = ${new Date(nodeData?.date).toLocaleString('default', {
                      month: 'short',
                    })}, `}
                    <br />
                    {`year = {${new Date(nodeData?.date).getFullYear()}}`}
                  </div>
                  {'}'}
                </Code>
              </Dialog.Body>
              <Dialog.Footer>
                <StyledButton
                  variant="outline"
                  text="Close"
                  onClick={zoteroModalDisclosure.onClose}
                />
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </motion.div>
  );

  function CardTags() {

    const availableResources = {
      articles: [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6_civil',
        '6_criminal',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '17',
        '18',
        '34',
        '35',
        '46',
      ],
      protocols: ['1-7', '1-1', '2-7', '2-4', '2-1', '3-4', '3-1', '4-7', '4-4'],
    };

    const resourceExists = (type: string | undefined, value: string) => {

      if (type === 'article') {
        const mainArticleNumber = value.split(/[-.]/)[0];


        // Special case for article 6
        if (mainArticleNumber === '6') {
          return (
            availableResources.articles.includes('6_civil') ||
            availableResources.articles.includes('6_criminal')
          );
        }
        return availableResources.articles.includes(mainArticleNumber);
      } else if (type === 'protocol') {
        const protocolNumber = value.substring(1);
        return availableResources.protocols.includes(protocolNumber);
      }
      return false;
    };

    const handleResourceNavigation = (type: string | undefined, value: string) => {
      if (!resourceExists(type, value)) {
        toaster.create({
          title: 'Resource not available',
          description: `This ${type} resource (${value}) is not available yet.`,
          type: 'warning',
          duration: 4000,
          closable: true,
        });
        return;
      }

      const mainArticleNumber = type === 'article' ? value.split(/[-.]/)[0] : value;

      if (type === 'article' && mainArticleNumber === '6') {
        article6ModalDisclosure.onOpen();
        return;
      }

      const resourceId = getResourceId(type, value);
      navigate(`/resources/${resourceId}`);
    };

    // Handle selection from modal
    const handleArticle6Selection = (subtype: 'civil' | 'criminal') => {
      const resourceId = `hudoc:article_6_${subtype}`;
      navigate(`/resources/${resourceId}`);
      article6ModalDisclosure.onClose();
    };

    const getResourceId = (type: string, value: string) => {
      if (type === 'protocol') {
        const protocolNumber = value.substring(1);
        return `hudoc:protocol_${protocolNumber}`;
      } else if (type === 'article') {
        const mainArticleNumber = value.split(/[-.]/)[0];
        return `hudoc:article_${mainArticleNumber}`;
      } else if (type === 'keyword') {
        return `keyword_${value.toLowerCase().replace(/\s+/g, '_')}`;
      }
      return value;
    };

    return (
      <>
        <Dialog.Root
          open={article6ModalDisclosure.open}
          onOpenChange={article6ModalDisclosure.setOpen}
        >
          <Portal>
            <Dialog.Positioner>
              <Dialog.Backdrop />
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Article 6 Selection</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Text>Please select the type of Article 6 you want to view:</Text>
                </Dialog.Body>
                <Dialog.Footer>
                  <Button colorScheme="blue" onClick={() => handleArticle6Selection('civil')}>
                    Article 6 (Civil)
                  </Button>
                  <Button colorScheme="purple" onClick={() => handleArticle6Selection('criminal')}>
                    Article 6 (Criminal)
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

        <HStack flexWrap={'wrap'}>
          {nodeData?.specific_properties.importance ? (
            <Tooltip content={Importance[nodeData?.specific_properties.importance].tooltip}>
              <Tag.Root variant={'solid'} bgColor={'blue.900'} cursor={'default'}>
                {Importance[nodeData?.specific_properties.importance].label}
              </Tag.Root>
            </Tooltip>
          ) : null}

          {/* Violation tags */}
          {nodeData?.specific_properties.violation
            ? nodeData?.specific_properties.violation.split(';').map((violation, index) => {

              const articleTooltip = Object.hasOwn(Article, violation) ? Article[violation].tooltip : violation;
              const articleLabel = Object.hasOwn(Article, violation) ? Article[violation].label : violation;
              const articleType = Object.hasOwn(Article, violation) ? Article[violation].type : violation;

              return (
                <Tooltip
                  content={`There has been found a violation of ${articleTooltip}`}
                  key={violation + index}
                >
                  <Tag.Root
                    onClick={(e) => {
                      handleResourceNavigation(articleType, violation);
                      e.stopPropagation();
                    }}
                    bgColor={'red.600'}
                    color={'white'}
                    cursor="pointer"
                  >
                    {articleLabel}
                  </Tag.Root>
                </Tooltip>
              );
            })
            : null}

          {/* Non-violation tags */}
          {nodeData?.specific_properties.nonviolation
            ? nodeData?.specific_properties.nonviolation.split(';').map((nonviolation, index) => {

              const articleTooltip = Object.hasOwn(Article, nonviolation) ? Article[nonviolation].tooltip : nonviolation;
              const articleLabel = Object.hasOwn(Article, nonviolation) ? Article[nonviolation].label : nonviolation;
              const articleType = Object.hasOwn(Article, nonviolation) ? Article[nonviolation].type : nonviolation;


              return (
                <Tooltip
                  content={`There has not been found a violation of ${articleTooltip}`}
                  key={nonviolation + index}
                >
                  <Tag.Root
                    onClick={(e) => {
                      handleResourceNavigation(articleType, nonviolation);
                      e.stopPropagation();
                    }}
                    bgColor={'green.600'}
                    color={'white'}
                    cursor="pointer"
                  >
                    {articleLabel}
                  </Tag.Root>
                </Tooltip>
              );

            })
            : null}
        </HStack>
      </>
    );
  }

  function CardMenu() {
    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Menu.Root
          open={contextMenuDisclosure.open}
          onOpenChange={(details: { open: boolean }) => {
            contextMenuDisclosure.setOpen(details.open);
          }}
        >
          <Menu.Trigger
            asChild
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <Icon
              color="secondaryText"
              boxSize={5}
              padding={'2px'}
              cursor={'pointer'}
              as={MoreVertical}
            // color="textOnDark"
            />
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content backgroundColor="cardDefault">
                <Menu.Root>
                  <Menu.TriggerItem
                    css={{
                      '&[data-state="open"]': {
                        background: 'cardHover',
                      },
                    }}
                    value="save"
                    color="secondaryText"
                    _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                    }}
                    cursor={'pointer'}
                  >
                    <Icon boxSize={5} padding={'2px'} cursor={'pointer'} as={FolderIcon} />
                    Save to Folder
                    <Icon boxSize={5} padding={'2px'} cursor={'pointer'} as={ChevronRight} />
                  </Menu.TriggerItem>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content backgroundColor="cardDefault">
                        {Object.values(folders).map((folder) => {
                          return (
                            <Menu.Item
                              color="secondaryText"
                              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                              id={`save-document-to-folder`}
                              key={folder.id}
                              value={folder.id}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleSave(e, folder.id, folder.name);
                              }}
                              cursor={'pointer'}
                            >
                              {folder.name}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>

                <Menu.Item
                  id={`open-document-${props.id}`}
                  value="open"
                  color="secondaryText"
                  _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    window.open(nodeData?.url, '_blank')?.focus();
                  }}
                  cursor={'pointer'}
                >
                  <Icon boxSize={5} padding={'2px'} cursor={'pointer'} as={ExternalLink} />
                  Open Document
                </Menu.Item>

                <Menu.Item
                  id={`cite-document-${props.id}`}
                  value="cite"
                  color="secondaryText"
                  _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    zoteroModalDisclosure.onOpen();
                  }}
                  cursor={'pointer'}
                >
                  <Icon boxSize={5} padding={'2px'} cursor={'pointer'} as={PenTool} />
                  Cite
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        {props.onClose ? (
          <Icon
            onClick={props.onClose}
            cursor={'pointer'}
            boxSize={5}
            as={X}
            color={'red.800'}
            marginLeft={'10px'}
          />
        ) : null}
      </div>
    );
  }
}

function SummaryText(props: { id: string; type: SummaryType }) {
  const { data, error, isLoading, isError } = useGetSummaryQuery(
    { id: props.id, type: props.type },
    { skip: !props.id },
  );

  useEffect(() => {
    if (isError) {
      console.error('Error fetching summary:', error);
    }
  }, [isError, error]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Tooltip content="This content is AI generated. Press the three dots and read the original document for more information.">
        <HStack>
          <Icon as={AISparcles} />
          <Text fontWeight={600} fontSize="14px" color={'gray.700'}>
            Summary{' '}
          </Text>
        </HStack>
      </Tooltip>

      {isLoading ? (
        <SkeletonText />
      ) : (
        <HStack>
          {data ? (
            data.split('\n').map((line, i) => (
              <Text key={`summary_${i}_text`} fontSize="14px" whiteSpace={'normal'}>
                {line}
                <br key={`summary_${i}_break`} />
              </Text>
            ))
          ) : (
            <Text fontSize="14px" whiteSpace={'normal'}>
              No summary available
            </Text>
          )}
        </HStack>
      )}

    </div>
  );
}
